
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export const useVoucherCode = () => {
  const [voucherCode, setVoucherCode] = useState<string>("");
  const [isValidatingVoucher, setIsValidatingVoucher] = useState<boolean>(false);
  const [validVoucher, setValidVoucher] = useState<{
    id: string;
    code: string;
    discount: {
      type: "percent" | "amount";
      value: number;
    };
  } | null>(null);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const { toast } = useToast();

  const validateVoucher = async () => {
    if (!voucherCode.trim()) {
      setVoucherError("Please enter a voucher code");
      return;
    }

    setIsValidatingVoucher(true);
    setVoucherError(null);

    try {
      // First check if the user is authenticated
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        setVoucherError("You need to be logged in to apply a voucher");
        setIsValidatingVoucher(false);
        return;
      }

      // Call Stripe API to validate the promotion code with the JWT token
      const { data, error } = await supabase.functions.invoke('validate-promotion-code', {
        body: { 
          code: voucherCode.trim() 
        },
        // Pass the JWT token in the authorization header
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`
        }
      });

      if (error) {
        if (error.message.includes("429")) {
          throw new Error("Too many attempts. Please try again later.");
        }
        throw new Error(error.message);
      }

      if (!data?.valid) {
        setVoucherError(data?.message || "Invalid voucher code");
        setValidVoucher(null);
        return;
      }

      setValidVoucher({
        id: data.id,
        code: voucherCode,
        discount: {
          type: data.amount_off ? "amount" : "percent",
          value: data.amount_off || data.percent_off
        }
      });

      toast({
        title: "Voucher applied!",
        description: data.amount_off 
          ? `$${(data.amount_off / 100).toFixed(2)} discount applied` 
          : `${data.percent_off}% discount applied`,
        duration: 5000
      });
    } catch (error: any) {
      console.error("Error validating voucher:", error);
      setVoucherError(error.message || "Failed to validate voucher");
      setValidVoucher(null);
    } finally {
      setIsValidatingVoucher(false);
    }
  };

  const clearVoucher = () => {
    setVoucherCode("");
    setValidVoucher(null);
    setVoucherError(null);
  };

  return {
    voucherCode,
    setVoucherCode,
    validateVoucher,
    clearVoucher,
    isValidatingVoucher,
    validVoucher,
    voucherError
  };
};
